import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import ConfirmModal from "../../../../components/ConfirmModal";
import { useToast } from "../../../../components/Toast";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import { GlobeIcon } from "../../../../icons/Globe";
import { localeOf } from "../../../../services/i18ln/format";
import { deleteLink, errorCodeOf, LINK_ERRORS, setLinkStatus, ShortLink } from "../../../../services/links";
import { useMenu } from "../../useMenu";
import { Actions, Card, CopyButton, Favicon, Info, Menu, Meta, Status, Tag, Tags, TitleRow } from "./styles";

const prefix = "pages.links";

/** The site of an address (loja.com.br), or the address itself when it isn't one. */
export const siteOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

// Two sheets, one over the other (as the other icons of the panel: 16px grid, 18px on screen)
export const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="M10.5 3.5V3.5C10.5 2.95 10.05 2.5 9.5 2.5H4C3.17 2.5 2.5 3.17 2.5 4V9.5C2.5 10.05 2.95 10.5 3.5 10.5V10.5"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

/** A date as the language writes it (day first in Portuguese: 25/12/2026; month first in English: 12/25/2026). */
export const formatDate = (iso: string, language: string) => new Intl.DateTimeFormat(language).format(new Date(iso));

// A click on a button of the card does its thing, not open the link
const stop = (event: React.SyntheticEvent) => event.stopPropagation();

type Props = {
  link: ShortLink;
  /** A tag of the link picked: the list shows only the links with it. */
  onTag: (tag: string) => void;
  /** The link as it is after a change (paused, resumed). */
  onChange: (link: ShortLink) => void;
  onDeleted: (id: string) => void;
};

/**
 * A Keepo Link in the list, drawn as a Keepo's card in Meus Keepos (opened with a click anywhere on it): its site,
 * the short link and where it goes, whether it is on, when it was made, its clicks and tags; and, at the end, copy
 * and its actions (edit, pause or resume, delete).
 */
export const LinkCard = ({ link, onTag, onChange, onDeleted }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const titleId = useId();
  const title = link.title || siteOf(link.destination);
  const [iconFailed, setIconFailed] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  // the menu: focus goes to its first item, arrows move it, Esc or a click outside closes it
  const { open: menuOpen, setOpen: setMenuOpen, ref: menuRef } = useMenu();

  // what was done, or what couldn't be, in a toast
  const failed = (what: "copyError" | "pauseError" | "resumeError" | "deleteError", reason?: string) =>
    toast.error(t(`${prefix}.${what}`), { description: reason ?? t(`${prefix}.tryAgain`) });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.shortUrl);
      toast.info(t(`${prefix}.copiedToast`), { description: link.shortUrl.replace(/^https?:\/\//, "") });
    } catch {
      failed("copyError");
    }
  };

  const run = async (action: () => Promise<void>, error: "pauseError" | "resumeError" | "deleteError") => {
    setMenuOpen(false);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      const code = errorCodeOf(caught);
      // a link made unsafe (or gone) since the list loaded: say that, not "try again"
      failed(
        error,
        code === LINK_ERRORS.unsafeDestination
          ? t(`${prefix}.unsafeHint`)
          : code === LINK_ERRORS.notFound
            ? t(`${prefix}.notFound`)
            : undefined
      );
      if (code === LINK_ERRORS.notFound) onDeleted(link.id);
    } finally {
      setBusy(false);
    }
  };

  const toggle = () =>
    run(
      async () => onChange(await setLinkStatus(link.id, link.status === "paused" ? "active" : "paused")),
      link.status === "paused" ? "resumeError" : "pauseError"
    );

  const remove = () =>
    run(async () => {
      setConfirmDelete(false);
      await deleteLink(link.id);
      onDeleted(link.id);
    }, "deleteError");

  const status = link.safety === "unsafe" ? "unsafe" : link.status === "paused" ? "paused" : "active";

  return (
    <Card aria-labelledby={titleId} aria-busy={busy} onClick={() => navigate(`/links/${link.id}`)}>
      <Favicon aria-hidden={iconFailed || !link.favicon}>
        {link.favicon && !iconFailed ? (
          <img src={link.favicon} alt="" onError={() => setIconFailed(true)} />
        ) : (
          <GlobeIcon />
        )}
      </Favicon>

      <Info>
        <TitleRow>
          <h4>
            <Link id={titleId} to={`/links/${link.id}`} onClick={stop}>
              {title}
            </Link>
          </h4>
        </TitleRow>
        <p className="url">
          <span className="short">{link.shortUrl.replace(/^https?:\/\//, "")}</span>
          <span aria-hidden="true"> → </span>
          <span title={link.destination}>{link.destination}</span>
        </p>
        <Meta>
          <Status className={status} title={status === "unsafe" ? t(`${prefix}.unsafeHint`) : undefined}>
            {t(`${prefix}.${status}`)}
          </Status>
          {link.createdAt && (
            <span>{t(`${prefix}.createdAt`, { date: formatDate(link.createdAt, i18n.language) })}</span>
          )}
          {/* no clicks: "0 cliques" (the plural rule of Portuguese would say "0 clique") */}
          <span>
            {link.clicks
              ? t(`${prefix}.clicks`, {
                  count: link.clicks,
                  value: link.clicks.toLocaleString(localeOf(i18n.language)),
                })
              : t(`${prefix}.noClicks`)}
          </span>
        </Meta>
        {/* on a line of their own under the status: many tags wrap here, not in the status line */}
        {link.tags.length > 0 && (
          <Tags role="group" aria-label={t(`${prefix}.tags`)}>
            {link.tags.map(tag => (
              <Tag
                key={tag}
                type="button"
                aria-label={t(`${prefix}.filterByTag`, { tag })}
                onClick={event => {
                  stop(event);
                  onTag(tag);
                }}>
                {tag}
              </Tag>
            ))}
          </Tags>
        )}
      </Info>

      <Actions onClick={stop}>
        <CopyButton
          type="button"
          className="copy"
          aria-label={t(`${prefix}.copy`)}
          title={t(`${prefix}.copy`)}
          onClick={copy}>
          <CopyIcon />
        </CopyButton>
        <Menu ref={menuRef}>
          <button
            type="button"
            className="more"
            aria-label={t(`${prefix}.actions`, { title })}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            disabled={busy}
            onClick={() => setMenuOpen(open => !open)}>
            <span aria-hidden="true">⋯</span>
          </button>
          {menuOpen && (
            <div role="menu" className="items">
              <button type="button" role="menuitem" disabled={busy} onClick={() => navigate(`/links/${link.id}/edit`)}>
                {t(`${prefix}.edit`)}
              </button>
              {/* an unsafe link can't be turned on: the API refuses */}
              {(link.status !== "paused" || link.safety !== "unsafe") && (
                <button type="button" role="menuitem" disabled={busy} onClick={toggle}>
                  {t(`${prefix}.${link.status === "paused" ? "resume" : "pause"}`)}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="danger"
                disabled={busy}
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmDelete(true);
                }}>
                {t(`${prefix}.delete`)}
              </button>
            </div>
          )}
        </Menu>
        <span className="chevron">
          <ChevronRightIcon width={14} height={14} color="currentColor" />
        </span>

        {confirmDelete && (
          <ConfirmModal
            type="DANGER"
            title={t(`${prefix}.deleteConfirm.title`)}
            descriptions={[
              t(`${prefix}.deleteConfirm.description`, { url: link.shortUrl.replace(/^https?:\/\//, "") }),
            ]}
            confirmButtonTitle={t(`${prefix}.deleteConfirm.confirm`)}
            cancelTitle={t(`${prefix}.deleteConfirm.cancel`)}
            onConfirm={remove}
            onCancel={() => setConfirmDelete(false)}
          />
        )}
      </Actions>
    </Card>
  );
};
